import React from "react";
import Modal from "./Modal";
import placeholderImage from "../img/placeholder-img.png";

const AddItemModal = ({
  showAddItemModal,
  handleCloseModal,
  handleAddSubmit,
  photoSrc,
  handlePhotoChange,
  handleRemovePhoto,
  photo,
  itemName,
  setItemName,
  itemDescription,
  setItemDescription,
  price,
  setPrice,
  categoryId,
  setCategoryId,
  subcategoryId,
  setSubcategoryId,
  categories,
  requiredOptions,
  optionalOptions,
  handleRequiredOptionSelect,
  selectedRequiredOptions,
  handleRemoveRequiredOption,
  newRequiredOptions,
  handleNewOptionNameChange,
  handleNewOptionPriceChange,
  handleRemoveNewRequiredOption,
  handleAddNewRequiredOption,
  handleOptionalOptionSelect,
  selectedOptionalOptions,
  handleRemoveOptionalOption,
  newOptionalOptions,
  handleNewOptionalOptionNameChange,
  handleNewOptionalOptionPriceChange,
  handleRemoveNewOptionalOption,
  handleAddNewOptionalOption,
  addingItem,
}) => {
  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  const disabledInputClass =
    "w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none";

  const sectionTitleClass = "text-sm font-semibold text-slate-900";

  const getInputClass = (disabled = false) =>
    disabled ? disabledInputClass : inputClass;

  return (
    showAddItemModal && (
      <Modal
        title="Add Menu Item"
        onClose={addingItem ? undefined : handleCloseModal}
        onSubmit={handleAddSubmit}
        submitButtonText={addingItem ? "Adding..." : "Add Menu Item"}
        showCancel={false}
        showSubmitButton={true}
        className="modal-content"
        disableSubmit={addingItem}
      >
        <div className="max-h-[60dvh] overflow-y-auto pr-1">
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className={sectionTitleClass}>Photo</p>

              <img
                src={photoSrc || placeholderImage}
                alt="Menu Item"
                className="mx-auto mt-4 h-[150px] w-[250px] max-w-full rounded-2xl border border-slate-200 bg-white object-cover"
              />

              <input
                type="file"
                accept="image/jpeg, image/jpg, image/png, image/webp"
                onChange={handlePhotoChange}
                id="addItemFileInput"
                className="hidden"
                disabled={addingItem}
              />

              <div className="mt-4 flex items-center justify-center gap-3">
                <label
                  htmlFor={addingItem ? undefined : "addItemFileInput"}
                  className={`inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                    addingItem
                      ? "cursor-not-allowed bg-slate-300 text-white"
                      : "cursor-pointer bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  Choose File
                </label>

                {photo && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    disabled={addingItem}
                    className={`inline-flex items-center justify-center rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                      addingItem
                        ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                        : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                    }`}
                  >
                    Remove
                  </button>
                )}
              </div>

              <p className="mt-3 break-all text-center text-xs text-slate-500">
                {photo ? photo.name : "No file chosen"}
              </p>
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Item Name</label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                className={getInputClass(addingItem)}
                required
                disabled={addingItem}
              />
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Item Description</label>
              <textarea
                value={itemDescription}
                rows={4}
                onChange={(e) => setItemDescription(e.target.value)}
                className={`${getInputClass(addingItem)} resize-none`}
                disabled={addingItem}
              />
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Price (£)</label>
              <input
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={getInputClass(addingItem)}
                disabled={addingItem}
              />
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Category</label>
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubcategoryId("");
                }}
                className={getInputClass(addingItem)}
                required
                disabled={addingItem}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            {categoryId &&
              categories.find((cat) => String(cat.id) === String(categoryId))
                ?.subcategories?.length > 0 && (
                <div className="space-y-2">
                  <label className={sectionTitleClass}>Select Sub-category</label>
                  <select
                    value={subcategoryId}
                    onChange={(e) => setSubcategoryId(e.target.value)}
                    className={getInputClass(addingItem)}
                    disabled={addingItem}
                  >
                    <option value="">None</option>
                    {categories
                      .find((cat) => String(cat.id) === String(categoryId))
                      ?.subcategories?.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.category_name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="space-y-2">
                <p className={sectionTitleClass}>
                  Required Options (Radio Buttons)
                </p>
              </div>

              <div className="mt-4">
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    handleRequiredOptionSelect(id);
                    e.target.value = "";
                  }}
                  className={getInputClass(addingItem)}
                  disabled={addingItem}
                >
                  <option value="">Select Required Option</option>
                  {requiredOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.option_name} (£
                      {Number(option.option_price || 0).toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 space-y-3">
                {selectedRequiredOptions.map((option, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[1fr_auto]"
                  >
                    <input
                      type="text"
                      value={`${option.option_name} - £${option.option_price}`}
                      readOnly
                      className={disabledInputClass}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveRequiredOption(index)}
                      disabled={addingItem}
                      title="Delete Option"
                      className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                        addingItem
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}

                {newRequiredOptions.map((option, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[2fr_1fr_auto]"
                  >
                    <input
                      type="text"
                      value={option.option_name}
                      onChange={(e) => handleNewOptionNameChange(e, index)}
                      placeholder="Option Name"
                      className={getInputClass(addingItem)}
                      disabled={addingItem}
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={option.option_price}
                      onChange={(e) => handleNewOptionPriceChange(e, index)}
                      placeholder="Price"
                      className={getInputClass(addingItem)}
                      disabled={addingItem}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveNewRequiredOption(index)}
                      disabled={addingItem}
                      title="Delete Option"
                      className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                        addingItem
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddNewRequiredOption}
                disabled={addingItem}
                className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  addingItem
                    ? "cursor-not-allowed bg-slate-300 text-white"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Add Option
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="space-y-2">
                <p className={sectionTitleClass}>
                  Optional Options (Checkboxes)
                </p>
              </div>

              <div className="mt-4">
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    handleOptionalOptionSelect(id);
                    e.target.value = "";
                  }}
                  className={getInputClass(addingItem)}
                  disabled={addingItem}
                >
                  <option value="">Select Optional Option</option>
                  {optionalOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.option_name} (+£
                      {Number(option.option_price || 0).toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 space-y-3">
                {selectedOptionalOptions.map((option, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[1fr_auto]"
                  >
                    <input
                      type="text"
                      value={`${option.option_name} - £${option.option_price}`}
                      readOnly
                      className={disabledInputClass}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveOptionalOption(index)}
                      disabled={addingItem}
                      title="Delete Option"
                      className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                        addingItem
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}

                {newOptionalOptions.map((option, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[2fr_1fr_auto]"
                  >
                    <input
                      type="text"
                      value={option.option_name}
                      onChange={(e) =>
                        handleNewOptionalOptionNameChange(e, index)
                      }
                      placeholder="Option Name"
                      className={getInputClass(addingItem)}
                      disabled={addingItem}
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={option.option_price}
                      onChange={(e) =>
                        handleNewOptionalOptionPriceChange(e, index)
                      }
                      placeholder="Price"
                      className={getInputClass(addingItem)}
                      disabled={addingItem}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveNewOptionalOption(index)}
                      disabled={addingItem}
                      title="Delete Option"
                      className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                        addingItem
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddNewOptionalOption}
                disabled={addingItem}
                className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  addingItem
                    ? "cursor-not-allowed bg-slate-300 text-white"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Add Option
              </button>
            </div>
          </div>
        </div>
      </Modal>
    )
  );
};

export default AddItemModal;